import { useState, useEffect, useCallback, type ComponentType } from 'react';
import {
  BarChart3,
  PieChart as PieChartIcon,
  TrendingUp,
  Download,
  Filter,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { FormField } from '@/components/common/FormSection';
import { AmountText, amountToneClass, type AmountTone } from '@/components/finance-v2/FinanceBadges';
import { financeV2Service } from '../services/financeV2Service';
import type { Transaction, Category, CostCenter } from '../types/financeV2.types';

import { getErrorMessage } from '../utils/error.utils'
import { parseDbTimestamp } from '../utils/datetime'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

type PeriodFilter = 'current_month' | 'last_3_months' | 'last_6_months' | 'last_12_months' | 'custom';

interface PeriodRange {
  startDate: string;
  endDate: string;
}

const PERIOD_OPTIONS: Array<{ value: PeriodFilter; label: string }> = [
  { value: 'current_month', label: 'Mês Atual' },
  { value: 'last_3_months', label: 'Últimos 3 Meses' },
  { value: 'last_6_months', label: 'Últimos 6 Meses' },
  { value: 'last_12_months', label: 'Últimos 12 Meses' },
  { value: 'custom', label: 'Personalizado' }
];

/** Cor das barras sem cor própria da categoria e do "Não Categorizado". */
const DEFAULT_CATEGORY_COLOR = 'var(--chart-1)';
const UNCATEGORIZED_COLOR = 'var(--muted-foreground)';

interface CategoryItem {
  category: string;
  type: string;
  total: number;
  count: number;
  color: string;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const resultTone = (value: number): AmountTone => (value >= 0 ? 'income' : 'expense');

function EmptyState({ text }: { text: string }) {
  return <p className="py-8 text-center text-sm text-muted-foreground">{text}</p>;
}

interface SummaryCardProps {
  label: string;
  icon: ComponentType<{ className?: string }>;
  tone: AmountTone;
  value: number;
  footer: string;
}

function SummaryCard({ label, icon: Icon, tone, value, footer }: SummaryCardProps) {
  return (
    <Card size="sm">
      <CardContent className="flex h-full flex-col gap-2">
        <div className="flex items-center gap-3">
          <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0">
            <h3 className="truncate text-xs text-muted-foreground">{label}</h3>
            <AmountText tone={tone} className="text-lg font-semibold">{formatCurrency(value)}</AmountText>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{footer}</p>
      </CardContent>
    </Card>
  );
}

interface CategoryBreakdownProps {
  title: string;
  tone: AmountTone;
  items: CategoryItem[];
  total: number;
  emptyText: string;
}

function CategoryBreakdown({ title, tone, items, total, emptyText }: CategoryBreakdownProps) {
  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PieChartIcon className={`size-4 ${amountToneClass(tone)}`} aria-hidden="true" />
          <h2>{title}</h2>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length > 0 ? (
          <ul className="flex flex-col gap-3">
            {items.map((item, index) => {
              const percentage = (item.total / total) * 100;
              return (
                <li key={index}>
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-sm font-medium">{item.category}</span>
                    <AmountText tone={tone} className="shrink-0 text-sm font-semibold">{formatCurrency(item.total)}</AmountText>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{ width: `${percentage}%`, backgroundColor: item.color }}
                    />
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{item.count} transações</span>
                    <span className="tabular-nums">{percentage.toFixed(1)}%</span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState text={emptyText} />
        )}
      </CardContent>
    </Card>
  );
}

export function FinanceV2ReportsPage() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('current_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const getPeriodRange = (): PeriodRange => {
    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
    let startDate: Date;

    switch (periodFilter) {
      case 'current_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last_3_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        break;
      case 'last_6_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        break;
      case 'last_12_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        break;
      case 'custom':
        return {
          startDate: customStartDate || new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0],
          endDate: customEndDate || endDate.toISOString().split('T')[0]
        };
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const period = getPeriodRange();

      const [transactionsRes, categoriesRes, costCentersRes] = await Promise.all([
        financeV2Service.transactions.list({
          page: 1,
          limit: 1000,
          dueDateFrom: period.startDate,
          dueDateTo: period.endDate
        }),
        financeV2Service.categories.list({ page: 1, limit: 100 }),
        financeV2Service.costCenters.list({ page: 1, limit: 100 })
      ]);

      setTransactions(transactionsRes.data.transactions);
      setCategories(categoriesRes.data.categories);
      setCostCenters(costCentersRes.data.costCenters);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar dados dos relatórios'));
    } finally {
      setLoading(false);
    }
    // getPeriodRange usa periodFilter/customStartDate/customEndDate; estabilizar via deps explícitas
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter, customStartDate, customEndDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calculate metrics
  const paidTransactions = transactions.filter(t => t.status === 'PAID');

  const totalIncome = paidTransactions
    .filter(t => t.transactionType === 'INCOME')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const totalExpense = paidTransactions
    .filter(t => t.transactionType === 'EXPENSE')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const netResult = totalIncome - totalExpense;

  // Group by month for timeline chart
  const monthlyData = paidTransactions.reduce((acc, t) => {
    const dateStr = (t.paidDate || t.dueDate).slice(0, 7); // YYYY-MM stable key
    const [year, month] = dateStr.split('-');
    const monthLabel = new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short'
    });
    const monthKey = dateStr;

    if (!acc[monthKey]) {
      acc[monthKey] = { month: monthLabel, income: 0, expense: 0 };
    }

    if (t.transactionType === 'INCOME') {
      acc[monthKey].income += t.amount || 0;
    } else if (t.transactionType === 'EXPENSE') {
      acc[monthKey].expense += t.amount || 0;
    }

    return acc;
  }, {} as Record<string, { month: string; income: number; expense: number }>);

  const monthlyChartData = Object.entries(monthlyData)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, v]) => v);

  // Group by category
  const categoryData: CategoryItem[] = categories
    .map(cat => {
      const categoryTransactions = paidTransactions.filter(
        t => t.categoryId === cat.id
      );

      const total = categoryTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);

      return {
        category: cat.name,
        type: cat.type,
        total,
        count: categoryTransactions.length,
        color: cat.color || DEFAULT_CATEGORY_COLOR
      };
    })
    .filter(item => item.total > 0)
    .sort((a, b) => b.total - a.total);

  // Add uncategorized transactions
  const uncategorizedTransactions = paidTransactions.filter(t => !t.categoryId);

  const uncategorizedIncome = uncategorizedTransactions
    .filter(t => t.transactionType === 'INCOME')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const uncategorizedExpense = uncategorizedTransactions
    .filter(t => t.transactionType === 'EXPENSE')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  // Add uncategorized income to category data
  if (uncategorizedIncome > 0) {
    categoryData.push({
      category: 'Não Categorizado',
      type: 'INCOME',
      total: uncategorizedIncome,
      count: uncategorizedTransactions.filter(t => t.transactionType === 'INCOME').length,
      color: UNCATEGORIZED_COLOR
    });
  }

  // Add uncategorized expense to category data
  if (uncategorizedExpense > 0) {
    categoryData.push({
      category: 'Não Categorizado',
      type: 'EXPENSE',
      total: uncategorizedExpense,
      count: uncategorizedTransactions.filter(t => t.transactionType === 'EXPENSE').length,
      color: UNCATEGORIZED_COLOR
    });
  }

  const incomeCategoryData = categoryData.filter(c => c.type === 'INCOME').sort((a, b) => b.total - a.total);
  const expenseCategoryData = categoryData.filter(c => c.type === 'EXPENSE').sort((a, b) => b.total - a.total);

  // Group by cost center
  const costCenterData = costCenters
    .map(cc => {
      const ccTransactions = paidTransactions.filter(
        t => t.costCenterId === cc.id
      );

      const income = ccTransactions
        .filter(t => t.transactionType === 'INCOME')
        .reduce((sum, t) => sum + (t.amount || 0), 0);

      const expense = ccTransactions
        .filter(t => t.transactionType === 'EXPENSE')
        .reduce((sum, t) => sum + (t.amount || 0), 0);

      return {
        costCenter: cc.name,
        income,
        expense,
        net: income - expense,
        count: ccTransactions.length,
        budget: cc.budget || 0
      };
    })
    .filter(item => item.count > 0)
    .sort((a, b) => Math.abs(b.net) - Math.abs(a.net));

  const exportToCSV = () => {
    const csvHeader = 'Data,Descrição,Tipo,Valor,Categoria,Centro de Custo,Status\n';
    const csvRows = paidTransactions.map(t => {
      const category = categories.find(c => c.id === t.categoryId)?.name || '-';
      const costCenter = costCenters.find(cc => cc.id === t.costCenterId)?.name || '-';
      const date = parseDbTimestamp(t.paidDate || t.dueDate)?.toLocaleDateString('pt-BR') ?? '';
      return `${date},${t.description},${t.transactionType},${t.amount},${category},${costCenter},${t.status}`;
    }).join('\n');

    const csv = csvHeader + csvRows;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `relatorio-financeiro-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando relatórios...</span>
        <Skeleton className="h-8 w-64 max-w-full" />
        <Skeleton className="h-28" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6">
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      </div>
    );
  }

  const paidIncomeCount = paidTransactions.filter(t => t.transactionType === 'INCOME').length;
  const paidExpenseCount = paidTransactions.filter(t => t.transactionType === 'EXPENSE').length;

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <BarChart3 className="size-6 shrink-0 text-muted-foreground" />
            <h1 className="min-w-0 break-words text-2xl font-semibold">Relatórios Financeiros</h1>
          </div>
          <p className="text-sm text-muted-foreground">Análises e visualizações das suas finanças</p>
        </div>
        <Button onClick={exportToCSV}>
          <Download className="size-4" />
          Exportar CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="size-4 text-muted-foreground" aria-hidden="true" />
            <h2>Filtros de Período</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {PERIOD_OPTIONS.map(({ value, label }) => (
              <Button
                key={value}
                variant={periodFilter === value ? 'default' : 'outline'}
                aria-pressed={periodFilter === value}
                className="whitespace-normal"
                onClick={() => setPeriodFilter(value)}
              >
                {label}
              </Button>
            ))}
          </div>

          {periodFilter === 'custom' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Data Inicial" htmlFor="report-start-date">
                <Input
                  id="report-start-date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </FormField>
              <FormField label="Data Final" htmlFor="report-end-date">
                <Input
                  id="report-end-date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </FormField>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <SummaryCard label="Total Receitas" icon={ArrowUpRight} tone="income" value={totalIncome} footer={`${paidIncomeCount} transações`} />
        <SummaryCard label="Total Despesas" icon={ArrowDownRight} tone="expense" value={totalExpense} footer={`${paidExpenseCount} transações`} />
        <SummaryCard
          label="Resultado Líquido"
          icon={DollarSign}
          tone={resultTone(netResult)}
          value={netResult}
          footer={netResult >= 0 ? 'Superávit' : 'Déficit'}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="size-4 text-muted-foreground" aria-hidden="true" />
            <h2>Evolução Mensal</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {monthlyChartData.length > 0 ? (
            <div className="min-w-0 overflow-x-auto">
              <Table className="min-w-[480px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Mês</TableHead>
                    <TableHead className="text-right">Receitas</TableHead>
                    <TableHead className="text-right">Despesas</TableHead>
                    <TableHead className="text-right">Resultado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyChartData.map((data, index) => {
                    const result = data.income - data.expense;
                    return (
                      <TableRow key={index}>
                        <TableCell>{data.month}</TableCell>
                        <TableCell className="text-right"><AmountText tone="income">{formatCurrency(data.income)}</AmountText></TableCell>
                        <TableCell className="text-right"><AmountText tone="expense">{formatCurrency(data.expense)}</AmountText></TableCell>
                        <TableCell className="text-right">
                          <AmountText tone={resultTone(result)} className="font-semibold">{formatCurrency(result)}</AmountText>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState text="Nenhum dado disponível para o período selecionado" />
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CategoryBreakdown
          title="Receitas por Categoria"
          tone="income"
          items={incomeCategoryData}
          total={totalIncome}
          emptyText="Nenhuma receita registrada no período"
        />
        <CategoryBreakdown
          title="Despesas por Categoria"
          tone="expense"
          items={expenseCategoryData}
          total={totalExpense}
          emptyText="Nenhuma despesa registrada no período"
        />
      </div>

      {costCenterData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="size-4 text-muted-foreground" aria-hidden="true" />
              <h2>Análise por Centro de Custo</h2>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="min-w-0 overflow-x-auto">
              <Table className="min-w-[720px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Centro de Custo</TableHead>
                    <TableHead className="text-right">Receitas</TableHead>
                    <TableHead className="text-right">Despesas</TableHead>
                    <TableHead className="text-right">Resultado</TableHead>
                    <TableHead className="text-right">Orçamento</TableHead>
                    <TableHead className="text-center">Transações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costCenterData.map((data, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{data.costCenter}</TableCell>
                      <TableCell className="text-right"><AmountText tone="income">{formatCurrency(data.income)}</AmountText></TableCell>
                      <TableCell className="text-right"><AmountText tone="expense">{formatCurrency(data.expense)}</AmountText></TableCell>
                      <TableCell className="text-right">
                        <AmountText tone={resultTone(data.net)} className="font-semibold">{formatCurrency(data.net)}</AmountText>
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">
                        {data.budget > 0 ? formatCurrency(data.budget) : '-'}
                      </TableCell>
                      <TableCell className="text-center tabular-nums text-muted-foreground">{data.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
